/** Client-side LLD practice report generator. */
import { jsPDF } from 'jspdf';

interface Clarification { question: string; answer: string; }
interface Attribute { name: string; type: string; accessModifier: string; }
interface Parameter { name: string; type: string; }
interface Method { name: string; parameters: Parameter[]; returnType: string; accessModifier: string; }
interface EntityResponsibility { attributes: Attribute[]; methods: Method[]; }
interface Entity { id: string; name: string; description?: string; }
interface Relationship { sourceEntityId: string; targetEntityId: string; type: string; cardinality: string; label?: string; }
interface UMLElement { type: string; name: string; fields: { access: string; name: string; type: string }[]; methods: { access: string; signature: string }[]; }
interface FileNode { path: string; content: string; }
interface RequirementItem { text: string; category: 'functional' | 'non-functional' | 'assumption'; }
interface TestCase { name: string; category: 'functional' | 'edge-case'; input: string; expectedOutput: string; assertion: string; }

export interface ProblemPdfData {
 title: string;
 description: string;
 difficulty?: string;
 category?: string;
 mode: string;
 clarifications: Clarification[];
 requirements: RequirementItem[];
 entities: Entity[];
 responsibilities: Record<string, EntityResponsibility>;
 relationships: Relationship[];
 uml: { elements: UMLElement[]; connections: { fromId: string; toId: string; type: string; cardinality: string }[] };
 codeFiles: FileNode[];
 activeLanguage: string;
 testCases: TestCase[];
}

type RGB = [number, number, number];
export type ProblemPdfTheme = 'branded' | 'interview';

interface PdfPalette {
 ink: RGB; body: RGB; muted: RGB; faint: RGB; line: RGB; soft: RGB;
 emerald: RGB; emerald2: RGB; emeraldSoft: RGB; white: RGB; page: RGB;
 dark: RGB; darkRaised: RGB; code: RGB; headerText: RGB;
}

const DARK_PALETTE: PdfPalette = {
 ink: [235, 240, 237] as RGB,
 body: [190, 199, 194] as RGB,
 muted: [143, 154, 148] as RGB,
 faint: [101, 113, 106] as RGB,
 line: [47, 57, 51] as RGB,
 soft: [20, 26, 23] as RGB,
 emerald: [16, 185, 129] as RGB,
 emerald2: [52, 211, 153] as RGB,
 emeraldSoft: [6, 55, 40] as RGB,
 white: [255, 255, 255] as RGB,
 page: [11, 15, 13] as RGB,
 dark: [13, 17, 15] as RGB,
 darkRaised: [22, 28, 24] as RGB,
 code: [218, 226, 221] as RGB,
 headerText: [255, 255, 255] as RGB,
};

const INTERVIEW_PALETTE: PdfPalette = {
 ink: [24, 29, 26],
 body: [61, 70, 65],
 muted: [101, 112, 106],
 faint: [137, 147, 141],
 line: [213, 221, 216],
 soft: [246, 248, 247],
 emerald: [4, 120, 87],
 emerald2: [5, 150, 105],
 emeraldSoft: [231, 247, 239],
 white: [255, 255, 255],
 page: [255, 255, 255],
 dark: [248, 250, 249],
 darkRaised: [235, 240, 237],
 code: [35, 43, 39],
 headerText: [24, 29, 26],
};

const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN = 44;
const CONTENT_W = PAGE_W - MARGIN * 2;
const PAGE_BOTTOM = PAGE_H - 46;

const accessSymbol = (mod: string) => mod === 'public' ? '+' : mod === 'private' ? '-' : mod === 'protected' ? '#' : mod;

export function buildProblemPdf(data: ProblemPdfData, theme: ProblemPdfTheme = 'branded'): jsPDF {
 const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
 const C = theme === 'interview' ? INTERVIEW_PALETTE : DARK_PALETTE;
 let y = MARGIN;
 const setFill = (color: RGB) => doc.setFillColor(...color);
 const setText = (color: RGB) => doc.setTextColor(...color);
 const setDraw = (color: RGB) => doc.setDrawColor(...color);
 const entityName = (id: string) => data.entities.find((entity) => entity.id === id)?.name || 'Unknown';

 const pageHeading = () => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  setText(C.emerald);
  doc.text('LETSBOOSTUP / LLD PRACTICE', MARGIN, 25);
  doc.setFont('helvetica', 'normal');
  setText(C.faint);
  doc.text(data.title, PAGE_W - MARGIN, 25, { align: 'right' });
  setDraw(C.line);
  doc.line(MARGIN, 33, PAGE_W - MARGIN, 33);
 };

 const addPage = () => {
  doc.addPage();
  setFill(C.page);
  doc.rect(0, 0, PAGE_W, PAGE_H, 'F');
  pageHeading();
  y = 55;
 };

 const ensure = (height: number) => {
  if (y + height > PAGE_BOTTOM) addPage();
 };

 const linesFor = (text: string, width: number, size = 10) => {
  doc.setFontSize(size);
  return doc.splitTextToSize(text || '', width) as string[];
 };

 const paragraph = (text: string, options: { size?: number; color?: RGB; bold?: boolean; width?: number; indent?: number; leading?: number } = {}) => {
  const size = options.size ?? 10.2;
  const width = options.width ?? CONTENT_W;
  const indent = options.indent ?? 0;
  const leading = options.leading ?? size * 1.52;
  doc.setFont('helvetica', options.bold ? 'bold' : 'normal');
  doc.setFontSize(size);
  setText(options.color ?? C.body);
  const lines = doc.splitTextToSize(text || '', width - indent) as string[];
  lines.forEach((line) => { ensure(leading); doc.text(line, MARGIN + indent, y); y += leading; });
 };

 const section = (number: number, title: string, subtitle: string) => {
  // Keep the section title with enough of its first content block so a heading
  // is never stranded at the bottom of a page.
  ensure(96);
  y += 16;
  setFill(C.emeraldSoft);
  setDraw([180, 220, 201]);
  doc.roundedRect(MARGIN, y, 24, 24, 5, 5, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  setText(C.emerald);
  doc.text(String(number).padStart(2, '0'), MARGIN + 12, y + 15.5, { align: 'center' });
  doc.setFontSize(14);
  setText(C.ink);
  doc.text(title, MARGIN + 36, y + 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  setText(C.muted);
  doc.text(subtitle, MARGIN + 36, y + 24);
  y += 43;
 };

 const empty = (text: string) => {
  ensure(31);
  setFill(C.soft);
  setDraw(C.line);
  doc.roundedRect(MARGIN, y, CONTENT_W, 27, 4, 4, 'FD');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.2);
  setText(C.muted);
  doc.text(text, MARGIN + 10, y + 17);
  y += 35;
 };

 const label = (text: string, x: number, top: number, color: RGB = C.emerald) => {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  setText(color);
  doc.text(text.toUpperCase(), x, top);
 };

 const bullet = (text: string, x: number, width: number) => {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.3);
  setText(C.body);
  const lines = doc.splitTextToSize(text, width - 16) as string[];
  setFill(C.emerald2);
  doc.circle(x + 2, y - 2.5, 1.35, 'F');
  lines.forEach((line, index) => { doc.text(line, x + 11, y); if (index < lines.length - 1) y += 13; });
  y += 14.5;
 };

 const codeBlock = (filename: string, code: string) => {
  doc.setFont('courier', 'normal');
  doc.setFontSize(8.1);
  const wrapped: string[] = [];
  code.replace(/\t/g, '  ').split('\n').forEach((line) => {
   const pieces = doc.splitTextToSize(line || ' ', CONTENT_W - 22) as string[];
   wrapped.push(...pieces);
  });
  let cursor = 0;
  let continuation = false;
  const lineHeight = 11.2;
  const fullBlockHeight = 19 + wrapped.length * lineHeight + 22;

  // Prefer keeping a complete source file together. Split only when the file
  // itself is taller than a fresh content page.
  if (fullBlockHeight <= PAGE_BOTTOM - 55 && PAGE_BOTTOM - y < fullBlockHeight) {
   addPage();
  }

  while (cursor < wrapped.length) {
   if (PAGE_BOTTOM - y < 85) addPage();
   const available = PAGE_BOTTOM - y;
   const maxLines = Math.max(4, Math.floor((available - 31) / lineHeight));
   const chunk = wrapped.slice(cursor, cursor + maxLines);
   const headerH = 19;
   const bodyH = chunk.length * lineHeight + 14;
   setFill(C.darkRaised);
   doc.roundedRect(MARGIN, y, CONTENT_W, headerH, 4, 4, 'F');
   doc.setFont('courier', 'bold');
   doc.setFontSize(8);
   setText(C.emerald2);
   doc.text(`${filename}${continuation ? ' (continued)' : ''}`, MARGIN + 9, y + 12.5);
   y += headerH;
   setFill(C.dark);
   doc.rect(MARGIN, y, CONTENT_W, bodyH, 'F');
   doc.setFont('courier', 'normal');
   doc.setFontSize(8.1);
   setText(C.code);
   for (const line of chunk) {
    y += lineHeight;
    doc.text(line, MARGIN + 10, y);
   }
   y += 14;
   cursor += chunk.length;
   continuation = true;
   if (cursor < wrapped.length) addPage();
  }
  y += 8;
 };

 // Cover
 setFill(C.page);
 doc.rect(0, 0, PAGE_W, PAGE_H, 'F');
 setFill(C.emerald2);
 doc.rect(0, 0, 6, PAGE_H, 'F');
 doc.setFont('helvetica', 'bold');
 doc.setFontSize(9);
 setText(C.emerald2);
 doc.text('LETSBOOSTUP / LOW-LEVEL DESIGN', MARGIN, 68);
 doc.setFontSize(31);
 setText(C.ink);
 const titleLines = linesFor(data.title, CONTENT_W, 31).slice(0, 2);
 titleLines.forEach((line, index) => doc.text(line, MARGIN, 119 + index * 34));
 const coverTextY = 119 + titleLines.length * 34 + 11;
 doc.setFont('helvetica', 'normal');
 doc.setFontSize(10);
 setText(C.body);
 const descriptionLines = doc.splitTextToSize(data.description || 'Practice summary', CONTENT_W - 40) as string[];
 descriptionLines.slice(0, 4).forEach((line, index) => doc.text(line, MARGIN, coverTextY + index * 15));

 const nonEmptyFiles = data.codeFiles.filter((file) => (file.content || '').trim().length > 0);
 const metrics = [
  [String(data.requirements.length), 'REQUIREMENTS'],
  [String(data.entities.length), 'ENTITIES'],
  [String(data.relationships.length), 'RELATIONSHIPS'],
  [String(nonEmptyFiles.length), 'CODE FILES'],
 ];
 const metricY = 280;
 metrics.forEach(([value, name], index) => {
  const x = MARGIN + index * (CONTENT_W / 4);
  if (index) { setDraw(C.line); doc.line(x, metricY, x, metricY + 54); }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(19); setText(C.ink); doc.text(value, x + 12, metricY + 22);
  doc.setFontSize(6.8); setText(C.muted); doc.text(name, x + 12, metricY + 39);
 });
 setDraw(C.line);
 doc.line(MARGIN, metricY - 13, PAGE_W - MARGIN, metricY - 13);
 doc.line(MARGIN, metricY + 58, PAGE_W - MARGIN, metricY + 58);

 const meta = [data.mode && `${data.mode.toUpperCase()} MODE`, data.difficulty?.toUpperCase(), data.category?.toUpperCase(), data.activeLanguage.toUpperCase()].filter(Boolean) as string[];
 let metaX = MARGIN;
 meta.forEach((item) => {
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.2);
  const width = doc.getTextWidth(item) + 18;
  setFill(C.soft); setDraw(C.line); doc.roundedRect(metaX, 365, width, 20, 4, 4, 'FD');
  setText(C.body); doc.text(item, metaX + 9, 378); metaX += width + 7;
 });
 doc.setFont('helvetica', 'normal'); doc.setFontSize(8); setText(C.faint);
 doc.text(`Generated ${new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`, MARGIN, PAGE_H - 55);

 // Report body
 addPage();
 section(1, 'Problem brief', 'Scope and clarification');
 paragraph(data.description || 'No problem statement was provided.', { size: 10, leading: 15 });
 if (data.clarifications.length) {
  y += 8;
  data.clarifications.forEach((item) => {
   ensure(35);
   paragraph(item.question, { bold: true, size: 8.5, color: C.ink });
   paragraph(item.answer, { size: 8.5, color: C.muted });
   y += 4;
  });
 }

 section(2, 'Requirements', 'Functional scope, quality attributes and assumptions');
 const requirementGroups: { key: RequirementItem['category']; label: string }[] = [
  { key: 'functional', label: 'Functional' },
  { key: 'non-functional', label: 'Non-functional' },
  { key: 'assumption', label: 'Assumptions' },
 ];
 if (!data.requirements.length) empty('No requirements captured.');
 requirementGroups.forEach((group) => {
  const items = data.requirements.filter((requirement) => requirement.category === group.key);
  if (!items.length) return;
  ensure(25 + items.length * 13);
  label(`${group.label} (${items.length})`, MARGIN, y + 2);
  y += 14;
  items.forEach((item) => bullet(item.text, MARGIN, CONTENT_W));
  y += 5;
 });

 addPage();
 section(3, 'Domain model', 'Entities with owned fields and methods');
 if (!data.entities.length) empty('No entities identified.');
 data.entities.forEach((entity) => {
  const responsibility = data.responsibilities[entity.id] || { attributes: [], methods: [] };
  const fields = responsibility.attributes.map((attribute) => `${accessSymbol(attribute.accessModifier)} ${attribute.name}: ${attribute.type}`);
  const methods = responsibility.methods.map((method) => `${accessSymbol(method.accessModifier)} ${method.name}(${method.parameters.map((parameter) => `${parameter.name}: ${parameter.type}`).join(', ')}): ${method.returnType}`);
  const description = entity.description ? linesFor(entity.description, CONTENT_W - 26, 8.8) : [];
  const bodyLines = Math.max(1, fields.length, methods.length);
  const height = 42 + description.length * 12 + bodyLines * 13 + 18;
  ensure(height + 12);
  const top = y;
  setFill(C.soft); setDraw(C.line); doc.roundedRect(MARGIN, top, CONTENT_W, height, 5, 5, 'FD');
  setFill(C.emerald); doc.roundedRect(MARGIN, top, 3, height, 2, 2, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11.5); setText(C.ink); doc.text(entity.name, MARGIN + 15, top + 20);
  let contentTop = top + 34;
  if (description.length) {
   doc.setFont('helvetica', 'normal'); doc.setFontSize(8.8); setText(C.muted);
   description.forEach((line) => { doc.text(line, MARGIN + 15, contentTop); contentTop += 12; });
  }
  const colW = (CONTENT_W - 42) / 2;
  label('Fields', MARGIN + 15, contentTop + 4, C.muted);
  label('Methods', MARGIN + 27 + colW, contentTop + 4, C.muted);
  contentTop += 16;
  doc.setFont('courier', 'normal'); doc.setFontSize(8.2); setText(C.body);
  (fields.length ? fields : ['None defined']).forEach((line, index) => doc.text(line, MARGIN + 15, contentTop + index * 13));
  (methods.length ? methods : ['None defined']).forEach((line, index) => {
   const shortened = doc.splitTextToSize(line, colW - 6)[0];
   doc.text(shortened, MARGIN + 27 + colW, contentTop + index * 13);
  });
  y += height + 12;
 });

 addPage();
 section(4, 'Relationships', 'Direction, relationship type and cardinality');
 if (!data.relationships.length) empty('No relationships defined.');
 data.relationships.forEach((relationship) => {
  ensure(42);
  setDraw(C.line); doc.line(MARGIN, y + 35, PAGE_W - MARGIN, y + 35);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); setText(C.ink);
  doc.text(entityName(relationship.sourceEntityId), MARGIN, y + 17);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.8); setText(C.muted);
  const relation = `${relationship.type} ->`;
  doc.text(relation, MARGIN + 135, y + 17);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); setText(C.ink);
  doc.text(entityName(relationship.targetEntityId), MARGIN + 225, y + 17);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); setText(C.emerald);
  doc.text(relationship.cardinality.replace('-to-', ' to '), PAGE_W - MARGIN, y + 17, { align: 'right' });
  y += 36;
 });

 section(5, 'UML class view', 'Final structural representation');
 if (!data.uml.elements.length) empty('No UML elements created.');
 for (const element of data.uml.elements) {
  const bodyLines = Math.max(1, element.fields.length, element.methods.length);
  const cardHeight = 66 + bodyLines * 13;
  ensure(cardHeight + 12);
  const top = y;
  setDraw(C.line); setFill(C.soft); doc.roundedRect(MARGIN, top, CONTENT_W, cardHeight, 6, 6, 'FD');
  setFill(C.darkRaised); doc.roundedRect(MARGIN, top, CONTENT_W, 36, 6, 6, 'F');
  setFill(C.emerald2); doc.rect(MARGIN, top, CONTENT_W, 2, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7); setText(C.emerald2);
  doc.text(element.type.replace('-', ' ').toUpperCase(), MARGIN + 12, top + 14);
  doc.setFontSize(10.5); setText(C.headerText); doc.text(element.name, MARGIN + 12, top + 28);
  const colW = (CONTENT_W - 36) / 2;
  let fieldsY = top + 54;
  let methodsY = top + 54;
  label('Fields', MARGIN + 12, fieldsY, C.muted);
  label('Methods', MARGIN + 24 + colW, methodsY, C.muted);
  fieldsY += 14;
  methodsY += 14;
  doc.setFont('courier', 'normal'); doc.setFontSize(8.1); setText(C.body);
  (element.fields.length ? element.fields.map((field) => `${field.access} ${field.name}: ${field.type}`) : ['None']).forEach((line) => {
   doc.text(doc.splitTextToSize(line, colW - 8)[0], MARGIN + 12, fieldsY);
   fieldsY += 13;
  });
  (element.methods.length ? element.methods.map((method) => `${method.access} ${method.signature}`) : ['None']).forEach((line) => {
   doc.text(doc.splitTextToSize(line, colW - 8)[0], MARGIN + 24 + colW, methodsY);
   methodsY += 13;
  });
  y += cardHeight + 12;
 }

 addPage();
 section(6, 'Implementation', `${data.activeLanguage} source files`);
 if (!nonEmptyFiles.length) empty('No implementation written.');
 nonEmptyFiles.forEach((file) => codeBlock(file.path, file.content));

 const totalPages = doc.getNumberOfPages();
 for (let page = 2; page <= totalPages; page += 1) {
  doc.setPage(page);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); setText(C.faint);
  doc.text('LetsBoostUp / LLD Practice', MARGIN, PAGE_H - 22);
  doc.text(`${page - 1} / ${totalPages - 1}`, PAGE_W - MARGIN, PAGE_H - 22, { align: 'right' });
 }
 return doc;
}

export function generateProblemPdf(data: ProblemPdfData, theme: ProblemPdfTheme = 'branded'): void {
 const safe = (data.title || 'lld-problem').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
 const suffix = theme === 'interview' ? 'interview-submission' : 'branded-summary';
 buildProblemPdf(data, theme).save(`${safe}-${suffix}.pdf`);
}
